import { Box, Flex, Text, Badge, VStack, HStack } from "@chakra-ui/react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { useState, useMemo } from "react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { getShadeFromPercent } from "../../../../../utils/colors";

const EnergyByDeviceCard = ({ locationData, selectedDevices }) => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [expandedOthers, setExpandedOthers] = useState(false);

    const transformedData = useMemo(() => {
        if (!locationData?.children) return [];
        const visibleDevices = [];
        const allDevices = [];
        
        function collectDevices(device) {
            if (
                typeof device?.value === "number" &&
                selectedDevices.some(x => x === device?.id)
            ) {
                visibleDevices.push({
                    name: device.title,
                    value: device.value,
                    percent: 0,
                    color: "",
                });
            }
            if (Array.isArray(device.children)) {
                device.children.forEach(child => collectDevices(child));
            }
        }

        locationData.children.forEach(device => collectDevices(device));
        if (visibleDevices.length > 1 ){
            visibleDevices.forEach(element => {
         
                if (visibleDevices.some(x => x.deviceId != element.deviceId && x.deviceParents.includes(element.deviceId))){
                    element.isVisible = false
                    allDevices.push(element)
                }else {
                    element.isVisible = true
                    allDevices.push(element)

                }
            });
        }else if( visibleDevices.length === 1){
            visibleDevices[0].isVisible = true
            allDevices.push(visibleDevices[0])
        }
        
        if (allDevices.length === 0) return [];
        allDevices.sort((a, b) => b.value - a.value);
        const total = locationData?.value;

        const topDevices = allDevices.slice(0, 5).map(d => {
            const percent = total > 0 ? (d.value / total) * 100 : 0;
            return {
                ...d,
                percent: percent.toFixed(1),
                color: getShadeFromPercent("#157fee", percent),
            };
        });

        const otherDevices = allDevices.slice(5).map(d => {
            const percent = total > 0 ? (d.value / total) * 100 : 0;
            return {
                ...d,
                percent: percent.toFixed(1),
                color: getShadeFromPercent("#157fee", percent),
            };
        });

        const otherTotal = otherDevices.reduce((acc, d) => acc + d.value, 0);
        const otherPercent = total > 0 ? (otherTotal / total) * 100 : 0;

        if (otherDevices.length > 0) {
            topDevices.push({
                name: "Others",
                value: otherTotal,
                percent: otherPercent.toFixed(1),
                color: "#9B9B9B",
                children: otherDevices,
            });
        }

        return topDevices;
    }, [locationData]);

    const handleClick = (index) => {
        if (transformedData[index].name === "Others") {
            setExpandedOthers(!expandedOthers);
        } else {
            setExpandedOthers(false);
        }
        setActiveIndex(index === activeIndex ? null : index);
    };

    return transformedData?.length > 0 && (
        <Box bg="white" mt={5} p={5} rounded="xl" boxShadow="sm" w="100%">
            <Text fontWeight="bold" fontSize="md" mb={4}>
                Energy Consumption by Device
            </Text>
            <Flex direction={{ base: "column", md: "row" }} gap={6} align="stretch">
                <VStack align="stretch" spacing={2} flex={1}>
                    {transformedData.map((item, i) => {
                        const isActive = activeIndex === i;
                        const isOthers = item.name === "Others";

                        return (
                            <Box key={item.name}>
                                <Flex
                                    justify="space-between"
                                    align="center"
                                    p={3}
                                    rounded="md"
                                    cursor="pointer"
                                    onClick={() => handleClick(i)}
                                    bg="#F5F6F7"
                                    border={isActive ? "2px solid" : "none"}
                                    borderColor={isActive ? "blue.500" : "transparent"}
                                    boxShadow={isActive ? "sm" : "none"}
                                >
                                    <HStack>
                                        {isOthers && (
                                            <ChevronDownIcon
                                                boxSize={4}
                                                color="black"
                                                transform={expandedOthers ? "rotate(180deg)" : "rotate(0deg)"}
                                                transition="transform 0.2s ease"
                                            />
                                        )}
                                        <Text fontSize="14px" fontWeight={700} fontFamily="Inter" color="#707070">{item.name}</Text>
                                    </HStack>
                                    <HStack>
                                        <Text color="#707070" fontSize="14px" fontWeight="400" fontFamily="Inter">
                                            {item.value.toFixed(2)} kWh
                                        </Text>
                                        <Badge
                                            bg={item.color}
                                            color="white"
                                            borderRadius="full"
                                            px={3}
                                            py={0.5}
                                            fontSize="sm"
                                        >
                                            {item.percent}%
                                        </Badge>
                                    </HStack>
                                </Flex>

                                {isOthers &&
                                    expandedOthers &&
                                    item.children?.map((child) => (
                                        <Flex
                                            key={child.name}
                                            ml={6}
                                            mt={1}
                                            p={2}
                                            rounded="md"
                                            bg="gray.100"
                                            justify="space-between"
                                            align="center"
                                        >
                                            <Text fontSize="14px" fontWeight={700} fontFamily="Inter" color="#707070">{child.name}</Text>
                                            <HStack>
                                                <Text color="#707070" fontSize="14px" fontWeight="400" fontFamily="Inter">
                                                    {child.value.toFixed(2)} kWh
                                                </Text>
                                                <Badge bg="#9B9B9B" color="white" borderRadius="full" px={3} py={0.5} fontSize="xs">
                                                    {child.percent}%
                                                </Badge>
                                            </HStack>
                                        </Flex>
                                    ))}
                            </Box>
                        );
                    })}
                </VStack>
                <Box flex={1} minH="300px" h="100%" display="flex" alignItems="center" justifyContent="center">
                    <Box w="100%" h="350px">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={transformedData} dataKey="value" outerRadius="90%">
                                    {transformedData.map((entry, index) => {
                                        const isFocused =
                                            activeIndex === null ||
                                            (activeIndex === index) ||
                                            (transformedData[activeIndex]?.name === "Others" &&
                                                transformedData[activeIndex]?.children?.some(child => child.name === entry.name));

                                        return (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={entry.color}
                                                fillOpacity={isFocused ? 1 : 0.2}
                                            />
                                        );
                                    })}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                </Box>
            </Flex>
        </Box>
    )
};

export default EnergyByDeviceCard;
